/**
 * Timestamp Formatting Utilities
 *
 * Provides relative time formatting for displaying timestamps
 * in a human-readable format (e.g., "2 min ago", "yesterday").
 */

/**
 * Format a date as a relative timestamp string.
 * Examples: "just now", "2 min ago", "1 hour ago", "yesterday", "3 days ago"
 *
 * @param date - The date to format
 * @param now - Optional current date for testing purposes
 * @returns Relative time string
 */
export function formatRelativeTime(date: Date, now: Date = new Date()): string {
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  // Future dates
  if (diffMs < 0) {
    return 'in the future';
  }

  // Less than a minute
  if (diffSec < 60) {
    return 'just now';
  }

  // Less than an hour
  if (diffMin < 60) {
    return diffMin === 1 ? '1 min ago' : `${diffMin} min ago`;
  }

  // Less than a day
  if (diffHour < 24) {
    return diffHour === 1 ? '1 hour ago' : `${diffHour} hours ago`;
  }

  // Yesterday (within 48 hours)
  if (diffDay === 1) {
    return 'yesterday';
  }

  // Less than a week
  if (diffDay < 7) {
    return `${diffDay} days ago`;
  }

  // Less than a month (approximate)
  if (diffDay < 30) {
    const weeks = Math.floor(diffDay / 7);
    return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
  }

  // Less than a year
  if (diffDay < 365) {
    const months = Math.floor(diffDay / 30);
    return months === 1 ? '1 month ago' : `${months} months ago`;
  }

  // A year or more
  const years = Math.floor(diffDay / 365);
  return years === 1 ? '1 year ago' : `${years} years ago`;
}

/**
 * Format execution time in a human-readable format.
 * Examples: "0.5s", "1.2s", "2m 30s"
 *
 * @param ms - Execution time in milliseconds
 * @returns Formatted execution time string
 */
export function formatExecutionTime(ms: number): string {
  if (ms < 1000) {
    // Less than 1 second, show as fraction
    return `${(ms / 1000).toFixed(1)}s`;
  }

  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) {
    // Less than a minute
    const fraction = ms % 1000;
    if (fraction > 0 && seconds < 10) {
      return `${(ms / 1000).toFixed(1)}s`;
    }
    return `${seconds}s`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (minutes < 60) {
    return remainingSeconds > 0
      ? `${minutes}m ${remainingSeconds}s`
      : `${minutes}m`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0
    ? `${hours}h ${remainingMinutes}m`
    : `${hours}h`;
}

// `formatRowCount` used to live here, duplicating an identical implementation
// under `workspace/common/utils/formatRowCount.ts`. It now lives only there —
// this module is about timestamps, and the row-count wording is shared by the
// status bars, the results grids and this feature's query history alike.
